import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Link,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useRecoilState } from "recoil";
import { refreshGlobal } from "../../../../atoms/refreshGlobal";
import TitleFlex from "../../vendasDigitadas/defaultComponents/titleFlex";
import {
  CNPJFormater,
  CPFFormater,
} from "../../../../defaultFunctions/formaters";
import { dateForList } from "../../../../constants/dataFormat";
import copy from "copy-to-clipboard";
import { customColors } from "../../../../atoms/customColors";
import { toast } from "react-toastify";
import { VendaBoleto } from "../PaymetLote";
import LoteStatusProgresso from "./LoteStatusProgresso";
import ListaBoletoAPI from "../../../../services/ListaBoleto";

interface ModalRevisãoVendaBoletoLoteProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  batchObjects: VendaBoleto[];
  setBatchObjects: (objects: VendaBoleto[]) => void;
  setBatchResponses: (responses: any[]) => void;
  setBoleto: (boleto: VendaBoleto) => void;
  initialValues: VendaBoleto;
  setLoadingState: (loading: boolean) => void;
  numBoletos: number;
  cliente: number;
  idLogin: number;
  handleBatchCreation: () => Promise<any>;
}

const getInterestDisplayText = (regrasJuros: any) => {
  if (!regrasJuros || !regrasJuros.mode || regrasJuros.mode === "SEM_JUROS") {
    return "Sem Juros";
  }

  const value = parseFloat(regrasJuros.value) / 100;

  if (regrasJuros.mode === "FIXED") {
    if (regrasJuros.frequency === "DAILY") {
      return `Valor Diário: R$ ${value.toFixed(2)}`;
    }
  } else if (regrasJuros.mode === "PERCENTAGE") {
    if (regrasJuros.frequency === "DAILY") {
      return `Percentual Diário: ${value.toFixed(3)}%`;
    } else if (regrasJuros.frequency === "MONTHLY") {
      return `Percentual Mensal: ${value.toFixed(2)}%`;
    } else if (regrasJuros.frequency === "ANNUALLY") {
      return `Percentual Anual: ${value.toFixed(2)}%`;
    }
  }

  return "Sem Juros";
};

const getMultaDisplayText = (regrasMulta: any) => {
  if (!regrasMulta || !regrasMulta.mode || regrasMulta.mode === "SEM_MULTA") {
    return "Sem Multa";
  }

  const value = parseFloat(regrasMulta.value) / 100;

  if (regrasMulta.mode === "FIXED") {
    return `Valor Fixo: R$ ${value.toFixed(2)}`;
  } else if (regrasMulta.mode === "PERCENTAGE") {
    return `Percentual: ${value.toFixed(2)}%`;
  }

  return "Sem Multa";
};

const getDescontoDisplayText = (regrasDesconto: any) => {
  if (
    !regrasDesconto ||
    !regrasDesconto.mode ||
    regrasDesconto.mode === "SEM_DESCONTO"
  ) {
    return "Sem Desconto";
  }

  const value = parseFloat(regrasDesconto.value) / 100;
  const dias = Number(regrasDesconto.diasAntesVencimento) || 0;
  const quando =
    dias > 0
      ? ` (até ${dias} dia(s) antes do vencimento)`
      : " (até o vencimento)";

  if (regrasDesconto.mode === "FIXED") {
    return `Valor Fixo: R$ ${value.toFixed(2)}${quando}`;
  } else if (regrasDesconto.mode === "PERCENTAGE") {
    return `Percentual: ${value.toFixed(2)}%${quando}`;
  }

  return "Sem Desconto";
};

export default function ModalRevisãoVendaBoletoLote({
  isOpen,
  setIsOpen,
  batchObjects,
  setBatchObjects,
  setBatchResponses,
  setBoleto,
  initialValues,
  setLoadingState,
  numBoletos,
  cliente,
  idLogin,
  handleBatchCreation,
}: ModalRevisãoVendaBoletoLoteProps) {
  const finalRef = React.useRef(null);
  const navigate = useNavigate();
  const [refresh, setRefreshGlobal] = useRecoilState(refreshGlobal);
  const colors = useRecoilState(customColors)[0];
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [loteId, setLoteId] = useState<string | null>(null);
  const [localBatchResponses, setLocalBatchResponses] = useState<any[]>([]);
  const [loadingState, setLocalLoadingState] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const onClose = () => {
    setIsOpen(false);
    setIsConfirmed(false);
    setLocalBatchResponses([]);
    setBatchObjects([]);
    setErrorMessage(null);
    setLoteId(null);
    setLoadingState(false);
  };

  const handleConfirm = async () => {
    setLocalLoadingState(true);
    setLoadingState(true);
    try {
      const result = await handleBatchCreation();
      // handleBatchCreation captura o erro internamente e retorna undefined.
      if (!result) {
        setErrorMessage("Erro ao criar o lote de boletos. Tente novamente.");
        return;
      }
      const boletos = result.boletos || [];
      setLocalBatchResponses(boletos);
      setBatchResponses(boletos);
      setLoteId(result.idLote || null);
      setIsConfirmed(true);
      setRefreshGlobal(0);
      toast.success(`${numBoletos} boleto(s) criado(s) com sucesso!`);
    } catch (error) {
      setErrorMessage("Erro ao criar o lote de boletos. Tente novamente.");
      toast.error("Erro ao criar o lote de boletos.");
    } finally {
      setLocalLoadingState(false);
      setLoadingState(false);
    }
  };

  const totalValue = batchObjects.reduce((sum, obj, index) => {
    return sum + (index === 0 ? obj.valorPrimeiraEmissao : obj.valor);
  }, 0);

  // Era um CSV de código de barras/URL, que não serve para nada no balcão:
  // ninguém paga um boleto a partir de uma planilha. Agora baixa o CARNÊ em
  // PDF, o mesmo arquivo do botão de download da lista — a rota
  // /bolepix/download/lote/{id} monta o PDF de todas as parcelas do lote.
  const [baixandoPdf, setBaixandoPdf] = useState(false);
  // O painel de progresso já consulta a core a cada 4s; em vez de uma consulta
  // própria, o botão do PDF escuta o fim dele. Enquanto não terminar, o carnê
  // sairia incompleto — e a apim2-web recusa de qualquer forma.
  const [geracaoConcluida, setGeracaoConcluida] = useState(false);

  const baixarPDF = async () => {
    if (!loteId) return;

    setBaixandoPdf(true);
    try {
      const token = window.localStorage.getItem("token") || "";
      await ListaBoletoAPI.downloadBatch(loteId, token);
      toast.success("Download do carnê iniciado!");
    } catch (error) {
      console.error("Erro ao baixar o carnê do lote:", error);
      toast.error("Não foi possível baixar o carnê em PDF.");
    } finally {
      setBaixandoPdf(false);
    }
  };

  const successfulBoletos = localBatchResponses.filter(
    (res) => res.idTransacao
  ).length;
  const failedBoletos = localBatchResponses.length - successfulBoletos;

  return (
    <Modal
      finalFocusRef={finalRef}
      isOpen={isOpen}
      onClose={onClose}
      size={"4xl"}
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader>
          <Flex w={"100%"} justifyContent={"center"}>
            <Text fontWeight={"bold"} color={colors.GREENMONEDD}>
              {isConfirmed
                ? "Lote de Boletos Criado!"
                : "Revisar Lote de Boletos"}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          {batchObjects.length > 0 && (
            <>
              <TitleFlex item={"Resumo do Lote"} />
              <Flex flexDir={"column"} px={4}>
                <Text>
                  <strong>Número de Boletos:</strong> {numBoletos}
                </Text>
                <Text>
                  <strong>Valor Total:</strong>{" "}
                  {totalValue.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Text>
                <Text>
                  <strong>Valor da Primeira Parcela:</strong>{" "}
                  {batchObjects[0].valorPrimeiraEmissao.toLocaleString(
                    "pt-BR",
                    { style: "currency", currency: "BRL" }
                  )}
                </Text>
                <Text>
                  <strong>Valor das Demais Parcelas:</strong>{" "}
                  {batchObjects[1]?.valor.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }) || "N/A"}
                </Text>
                <Text>
                  <strong>Descrição Base:</strong> {batchObjects[0].descricao}
                </Text>
                <Text>
                  <strong>Segunda Parcela no Mesmo Mês:</strong>{" "}
                  {batchObjects[0].mesesPular === -1 ? "Sim" : "Não"}
                </Text>
                {batchObjects[0].mesesPular !== -1 && (
                  <Text>
                    <strong>Meses a Pular:</strong> {batchObjects[0].mesesPular}
                  </Text>
                )}
              </Flex>

              <TitleFlex item={`Parcelas a Emitir (${batchObjects.length})`} />
              <Box
                maxH="320px"
                overflowY="auto"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                mx={4}
              >
                <Table variant="simple" size="sm">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th>Parcela</Th>
                      <Th>Vencimento</Th>
                      <Th>Limite Pgto</Th>
                      <Th isNumeric>Valor</Th>
                      <Th isNumeric>Desconto</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {batchObjects.map((obj, index) => {
                      const valorParcela =
                        index === 0 ? obj.valorPrimeiraEmissao : obj.valor;
                      const temDesc =
                        !!obj.RegrasDesconto &&
                        obj.RegrasDesconto.mode !== "SEM_DESCONTO";
                      // O desconto chega ×100 em qualquer modo. Em PERCENTAGE
                      // o número é um percentual — o abatimento em R$ depende
                      // da parcela, e é ele que interessa nesta coluna.
                      const descPercentual =
                        temDesc && obj.RegrasDesconto!.mode === "PERCENTAGE";
                      const descBruto = temDesc
                        ? parseFloat(String(obj.RegrasDesconto!.value)) / 100
                        : 0;
                      const descVal = descPercentual
                        ? (valorParcela * descBruto) / 100
                        : descBruto;
                      return (
                        <Tr key={index}>
                          <Td>
                            {index === 0 ? "1ª (entrada)" : `${index + 1}ª`}
                          </Td>
                          <Td>{moment(obj.vencimento).format(dateForList)}</Td>
                          <Td>
                            {obj.dataLimitePagamento
                              ? moment(obj.dataLimitePagamento).format(
                                dateForList
                              )
                              : "—"}
                          </Td>
                          <Td isNumeric>
                            {valorParcela.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </Td>
                          <Td isNumeric>
                            {temDesc
                              ? `- ${descVal.toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              })}${
                                descPercentual
                                  ? ` (${descBruto
                                    .toFixed(2)
                                    .replace(".", ",")}%)`
                                  : ""
                              }`
                              : "—"}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              <TitleFlex item={"Dados do Pagador"} />
              <Flex flexDir={"column"} px={4}>
                <Text>Nome do Pagador: {batchObjects[0].nomePagador}</Text>
                <Text>
                  Documento do Pagador:{" "}
                  {batchObjects[0].cpfPagador.length === 11
                    ? CPFFormater(batchObjects[0].cpfPagador)
                    : CNPJFormater(batchObjects[0].cpfPagador)}
                </Text>
                <Text>Email: {batchObjects[0].emailPagador}</Text>
                <Text>Número: {batchObjects[0].numeroPagador}</Text>
              </Flex>

              <TitleFlex item={"Endereço de Cobrança"} />
              <Flex flexDir={"column"} px={4}>
                <Text>
                  {`${batchObjects[0].enderRua}, ${batchObjects[0].enderNumero}/${batchObjects[0].enderBairro}-${batchObjects[0].enderCidade}-${batchObjects[0].enderEstado}`}
                </Text>
                <Text>CEP: {batchObjects[0].enderCEP}</Text>
                {batchObjects[0].enderComplemento && (
                  <Text>Complemento: {batchObjects[0].enderComplemento}</Text>
                )}
                {batchObjects[0].enderReferencia && (
                  <Text>Referência: {batchObjects[0].enderReferencia}</Text>
                )}
              </Flex>

              {batchObjects[0].permitirRegras && (
                <>
                  <TitleFlex item={"Regras de Juros, Multa e Desconto"} />
                  <Flex flexDir={"column"} px={4}>
                    <Text>
                      Juros:{" "}
                      {getInterestDisplayText(batchObjects[0].RegrasJuros)}
                    </Text>
                    <Text>
                      Multa: {getMultaDisplayText(batchObjects[0].RegrasMulta)}
                    </Text>
                    <Text>
                      Desconto:{" "}
                      {getDescontoDisplayText(batchObjects[0].RegrasDesconto)}
                    </Text>
                  </Flex>
                </>
              )}

              {isConfirmed && loteId && (
                <>
                  <TitleFlex item={"Geração das Parcelas"} />
                  <Box px={4}>
                    <LoteStatusProgresso
                      idLote={loteId}
                      // só libera o PDF com o carnê INTEIRO emitido: lote que
                      // terminou com parcela em falha também "conclui"
                      onConcluido={(r) =>
                        setGeracaoConcluida(r.total > 0 && r.emitidas >= r.total)
                      }
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter justifyContent={"space-evenly"}>
          {!isConfirmed ? (
            <>
              <Button
                background={colors.GRAY}
                color={"white"}
                mr={3}
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                bgColor={colors.BLUEMONEDDBUTTONS}
                color={colors.WHITE}
                isDisabled={loadingState}
                onClick={handleConfirm}
              >
                {loadingState ? (
                  <Spinner size="sm" />
                ) : (
                  `Confirmar Criação de ${numBoletos} Boleto${
                    numBoletos > 1 ? "s" : ""
                  }`
                )}
              </Button>
            </>
          ) : (
            <Flex direction="column" align="center" gap={2}>
              <Flex gap={3}>
                <Button
                  background={colors.GRAY}
                  color={"white"}
                  onClick={onClose}
                >
                  Fechar
                </Button>
                {loteId && (
                  <Button
                    bgColor={colors.GREENMONEDD}
                    color={colors.WHITE}
                    onClick={baixarPDF}
                    isLoading={baixandoPdf}
                    loadingText="Gerando PDF..."
                    isDisabled={!geracaoConcluida}
                    title={
                      geracaoConcluida
                        ? "Baixar o carnê completo em PDF"
                        : "Disponível quando todas as parcelas forem emitidas"
                    }
                  >
                    Baixar PDF
                  </Button>
                )}
              </Flex>

              {/* O carnê é montado a partir do que já está gravado: as parcelas
                  2..N emitem em background, então baixar antes do fim traz o
                  PDF incompleto. Melhor avisar do que devolver um carnê capenga
                  sem explicação. */}
              {loteId && !geracaoConcluida && (
                <Text fontSize="xs" color="orange.600" textAlign="center">
                  O download fica disponível quando todas as parcelas forem
                  emitidas — um carnê pela metade parece completo e é pior que
                  nenhum. Acompanhe acima; pode fechar esta janela, a geração
                  continua.
                </Text>
              )}
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
